import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PurchaseReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const history = useHistory();

  const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [fetched, setFetched]   = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData('ReportApi', `/PurchaseReport/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch purchase report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  // Summary totals
  const totalQty     = data.reduce((s, r) => s + (parseFloat(r.TotalQuantity) || 0), 0);
  const totalAmount  = data.reduce((s, r) => s + (parseFloat(r.TotalAmount)   || 0), 0);
  const totalPaid    = data.reduce((s, r) => s + (parseFloat(r.PaidAmount)    || 0), 0);
  const totalBalance = data.reduce((s, r) => s + (parseFloat(r.BalanceAmount) || 0), 0);

  const handleExport = async () => {
    if (!data.length) return;

    const now = new Date();
    const generatedAt = now.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const colHeaders = ['#', 'Bill No', 'Date', 'Supplier', 'Qty', 'Amount', 'Discount', 'Taxable',
      'CGST', 'SGST', 'IGST', 'Freight', 'Frt.GST%', 'Frt.GST',
      'Packing', 'Pkg.GST%', 'Pkg.GST', 'TCS', 'Total', 'Paid', 'Balance'];

    const dataRows = data.map((r, i) => [
      i + 1, r.BillNumber, fmtDate(r.BillDate), r.MasterName,
      parseFloat(r.TotalQuantity)||0, parseFloat(r.Amount)||0, parseFloat(r.DiscountAmount)||0, parseFloat(r.TaxableAmount)||0,
      parseFloat(r.CGSTAmount)||0, parseFloat(r.SGSTAmount)||0, parseFloat(r.IGSTAmount)||0,
      parseFloat(r.FreightAmount)||0, parseFloat(r.FreightGSTRate)||0, parseFloat(r.FreightGSTAmount)||0,
      parseFloat(r.PackingAmount)||0, parseFloat(r.PackingGSTRate)||0, parseFloat(r.PackingGSTAmount)||0,
      parseFloat(r.TCSAmount)||0, parseFloat(r.TotalAmount)||0, parseFloat(r.PaidAmount)||0, parseFloat(r.BalanceAmount)||0,
    ]);

    const totalsRow = [
      '', '', '', 'Totals',
      data.reduce((s,r)=>s+(parseFloat(r.TotalQuantity)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.Amount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.DiscountAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.TaxableAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.CGSTAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.SGSTAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.IGSTAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.FreightAmount)||0),0), '',
      data.reduce((s,r)=>s+(parseFloat(r.FreightGSTAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.PackingAmount)||0),0), '',
      data.reduce((s,r)=>s+(parseFloat(r.PackingGSTAmount)||0),0),
      data.reduce((s,r)=>s+(parseFloat(r.TCSAmount)||0),0),
      totalAmount, totalPaid, totalBalance,
    ];

    // Build sheet rows
    // Row 1: Report title (spans all cols)
    // Row 2: Company | val | Branch | val
    // Row 3: Date Range | val | Generated By | val | Generated Time | val
    // Row 4: blank
    // Row 5: column headers
    // Row 6+: data
    // Last: totals

    const numCols = colHeaders.length;

      await exportStyledExcel({
          reportTitle: 'PURCHASE REPORT',
          sheetName: 'Purchase Report',
          fileName: `PurchaseReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows: dataRows,
            totalsRow: totalsRow,
          columnWidths: [4,12,12,18,8,10,10,10,10,10,10,10,9,10,10,9,10,8,12,10,12],
      });
  };

  const th  = { padding: '8px 10px', textAlign: 'left',  fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
  const td  = { padding: '7px 10px', textAlign: 'left',  fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading purchase report..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #d4500a 0%, #e8734a 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Purchase Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Scrollable Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: 1100 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: '#e8734a', color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Bill No</th>
                <th style={th}>Date</th>
                <th style={th}>Supplier</th>
                {/* <th style={th}>Type</th>
                <th style={th}>GST Type</th> */}
                <th style={{...th, textAlign:'right'}}>Qty</th>
                <th style={{...th, textAlign:'right'}}>Amount</th>
                <th style={{...th, textAlign:'right'}}>Discount</th>
                <th style={{...th, textAlign:'right'}}>Taxable</th>
                <th style={{...th, textAlign:'right'}}>CGST</th>
                <th style={{...th, textAlign:'right'}}>SGST</th>
                <th style={{...th, textAlign:'right'}}>IGST</th>
                <th style={{...th, textAlign:'right'}}>Freight</th>
                <th style={{...th, textAlign:'right'}}>Frt.GST%</th>
                <th style={{...th, textAlign:'right'}}>Frt.GST</th>
                <th style={{...th, textAlign:'right'}}>Packing</th>
                <th style={{...th, textAlign:'right'}}>Pkg.GST%</th>
                <th style={{...th, textAlign:'right'}}>Pkg.GST</th>
                <th style={{...th, textAlign:'right'}}>TCS</th>
                <th style={{...th, textAlign:'right'}}>Total</th>
                <th style={{...th, textAlign:'right'}}>Paid</th>
                <th style={{...th, textAlign:'right'}}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={r.Id || i} style={{ background: i % 2 === 0 ? '#fff' : '#fbe9e7', borderBottom: '1px solid #f0f0f0' }}>
                  <td style={td}>{i + 1}</td>
                  <td style={{ ...td, fontWeight: 600, color: '#bf360c' }}>{r.BillNumber || '-'}</td>
                  <td style={td}>{fmtDate(r.BillDate)}</td>
                  <td style={{ ...td, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.MasterName || '-'}</td>
                  {/* <td style={td}>
                    <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: r.PurchaseType === 'Credit' ? '#fef3c7' : '#dbeafe', color: r.PurchaseType === 'Credit' ? '#d97706' : '#1d4ed8' }}>
                      {r.PurchaseType || '-'}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: '#f3f4f6', color: '#374151' }}>
                      {r.GstCalculationTypeName || '-'}
                    </span>
                  </td> */}
                  <td style={tdR}>{fmtQty(r.TotalQuantity)}</td>
                  <td style={tdR}>{(r.Amount)}</td>
                  <td style={tdR}>{(r.DiscountAmount)}</td>
                  <td style={tdR}>{(r.TaxableAmount)}</td>
                  <td style={tdR}>{(r.CGSTAmount)}</td>
                  <td style={tdR}>{(r.SGSTAmount)}</td>
                  <td style={tdR}>{(r.IGSTAmount)}</td>
                  <td style={tdR}>{(r.FreightAmount)}</td>
                  <td style={tdR}>{(parseFloat(r.FreightGSTRate)||0).toFixed(1)}%</td>
                  <td style={tdR}>{(r.FreightGSTAmount)}</td>
                  <td style={tdR}>{(r.PackingAmount)}</td>
                  <td style={tdR}>{(parseFloat(r.PackingGSTRate)||0).toFixed(1)}%</td>
                  <td style={tdR}>{(r.PackingGSTAmount)}</td>
                  <td style={tdR}>{(r.TCSAmount)}</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{(r.TotalAmount)}</td>
                  <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{(r.PaidAmount)}</td>
                  <td style={{ ...tdR, color: (parseFloat(r.BalanceAmount) || 0) > 0 ? '#dc2626' : '#374151', fontWeight: 600 }}>{(r.BalanceAmount)}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={23} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: 13 }}>
                    {fetched ? 'No purchase records found for the selected date range' : 'Select date range to load report'}
                  </td>
                </tr>
              )}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr style={{ background: '#ffccbc', color: '#bf360c', fontWeight: 700 }}>
                  <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalQty)}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.Amount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.DiscountAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.TaxableAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.CGSTAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.SGSTAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.IGSTAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.FreightAmount)||0),0))}</td>
                  <td style={tdR}>-</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.FreightGSTAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.PackingAmount)||0),0))}</td>
                  <td style={tdR}>-</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.PackingGSTAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(data.reduce((s,r) => s+(parseFloat(r.TCSAmount)||0),0))}</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                  <td style={tdR}>{fmt(totalPaid)}</td>
                  <td style={tdR}>{fmt(totalBalance)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>{/* end scrollable */}
      </div>
    </div>
  );
};

export default PurchaseReport;


